// @ts-check

const { formatCurrency, orderFeesPostCalculation, getCustomLabel } = require('../utils/feeService.utils');

/**
 * @description Escape HTML special characters to prevent XSS
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  // @ts-ignore
  return text.toString().replaceAll(/[&<>"']/g, (char) => map[char]);
}

/**
 * @typedef {import('../../types/PaymentConfirmationV2').PaymentConfirmationV2Props} PaymentConfirmationV2Props
 * @typedef {import('../../types/PaymentConfirmationV2').PaymentResponseData} PaymentResponseData
 * @typedef {import('../../types/paymentDataBuilder').PaymentData} PaymentData
 * @typedef {import('../../types/fees').CalculationViewResult} CalculationViewResult
 * @typedef {import('../../types/fees').DualPricingViewResult} DualPricingViewResult
 * @typedef {import('../../types/fees').Fee} Fee
 * @typedef {import('../../types/fees').DatabaseFeeSettings} DatabaseFeeSettings
 * @typedef {import('../../types/termsConditions').TermsConditions} TermsConditions
 *
 * @typedef {Object} ReceiptData
 * @property {string} [invoiceNumber] - Invoice number (optional)
 * @property {PaymentResponseData} paymentResponse - Payment response with transaction ID
 * @property {PaymentData} paymentData - Payment data including method and addresses
 * @property {string} customerName - Customer full name
 * @property {CalculationViewResult | null} calculationResult - Fee calculation result (CalculationViewResult only)
 * @property {DatabaseFeeSettings | null} feeSettings - Fee settings for custom labels (optional)
 * @property {string} [locationName] - Location/business name (optional)
 * @property {string} [locationImageUrl] - Location logo URL (optional)
 * @property {Date | string} [transactionDate] - Transaction date string (optional)
 */

/**
 * @fileoverview ReceiptService - Service for building HTML receipt strings
 *
 * @description
 * Generates HTML receipt content using inline styles following PrintReceipt patterns.
 * Mimics FeeDisplay component's behavior for fee rendering.
 * No data fetching - all data provided via constructor.
 *
 * @example
 * const receipt = new ReceiptService.Receipt(receiptData);
 * const htmlString = receipt.buildHTML();
 */

/**
 * @class Receipt
 * @description Service class for building HTML receipt strings with inline styling
 */
class Receipt {
  /**
   * @param {ReceiptData} data - Receipt data from PaymentConfirmationV2 props
   */
  constructor(data) {
    this.data = data;
  }

  /**
   * @description Build complete receipt as HTML string with inline styles
   * @returns {string} Complete HTML receipt string ready for printing
   */
  buildHTML() {
    const html = `
      <html>
        <head>
          <title>Payment Receipt</title>
        </head>
        <body onafterprint="self.close()">
          ${this._renderHeader()}
          ${this._renderGreeting()}
          ${this._renderTitle()}
          ${this._renderTransactionDetails()}
          ${this._renderFeeBreakdown()}
          ${this._renderFooter()}
        </body>
      </html>
    `;

    return html;
  }

  /**
   * @private
   * @description Render company logo header
   * @returns {string} Logo HTML
   */
  _renderHeader() {
    if (!this.data.locationImageUrl) return '';

    return `
      <div style="text-align: center">
        <img 
          alt="logo" 
          src="${escapeHtml(this.data.locationImageUrl)}" 
          width="100" 
          style="padding: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif; margin: 5px auto; display: block; width: 10%;" 
        />
      </div>
    `;
  }

  /**
   * @private
   * @description Render customer greeting
   * @returns {string} Greeting HTML
   */
  _renderGreeting() {
    if (!this.data.customerName) return '';

    return `
      <p style="padding: 0; margin: 0; box-sizing: border-box; font-family: sans-serif; font-size: 18px;">
        Hi <strong style="padding: 0; margin: 0; box-sizing: border-box; font-family: sans-serif; font-size: 18px;">${escapeHtml(this.data.customerName)}</strong>,
      </p>
    `;
  }

  /**
   * @private
   * @description Render receipt title
   * @returns {string} Title HTML
   */
  _renderTitle() {
    return `
      <h3 style="padding: 0; box-sizing: border-box; font-family: sans-serif; text-align: center; margin: 25px 0; font-size: 25px; font-weight: 700; color: rgba(53, 94, 170, 1);">
        Receipt
      </h3>
    `;
  }

  /**
   * @private
   * @description Render transaction details table
   * @returns {string} Transaction details HTML
   */
  _renderTransactionDetails() {
    const { invoiceNumber, paymentResponse, paymentData } = this.data;
    const transactionId = paymentResponse?.transactionId;
    const paymentType = this._getPaymentTypeDisplay();
    const maskedAccountNumber = paymentData?.paymentMethod?.maskedAccountNumber;
    const date = this._getFormattedDate();

    return `
      <table style="padding: 0; margin: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif; width: 100%; border-collapse: collapse;" width="100%">
        ${invoiceNumber ? this._renderDetailRow('Invoice Number', invoiceNumber) : ''}
        ${transactionId ? this._renderDetailRow('Transaction ID', transactionId) : ''}
        ${this.data.customerName ? this._renderDetailRow('Customer Name', this.data.customerName) : ''}
        ${paymentType ? this._renderDetailRow('Payment Type', paymentType) : ''}
        ${maskedAccountNumber ? this._renderDetailRow('Account Number', maskedAccountNumber) : ''}
        ${date ? this._renderDetailRow('Date', date) : ''}
      </table>
    `;
  }

  /**
   * @private
   * @description Render a single detail row
   * @param {string} label - Row label
   * @param {string} value - Row value
   * @returns {string} Row HTML
   */
  _renderDetailRow(label, value) {
    return `
      <tr style="padding: 0; margin: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif;">
        <td style="margin: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif; text-align: left; padding: 8px 0;">
          <strong style="padding: 0; margin: 0; box-sizing: border-box; font-size: 14px; font-family:sans-serif;">${escapeHtml(label)}</strong>
        </td>
        <td style="margin: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif; text-align: right; padding: 8px 0;">
          ${escapeHtml(value)}
        </td>
      </tr>
    `;
  }

  /**
   * @private
   * @description Check if calculation result is CalculationViewResult type
   * @param {CalculationViewResult | DualPricingViewResult | null} result
   * @returns {result is CalculationViewResult}
   */
  _isCalculationViewResult(result) {
    return result !== null && 'totalAmount' in result && 'fees' in result;
  }

  /**
   * @private
   * @description Render fee breakdown section
   * @returns {string} Fee breakdown HTML
   */
  _renderFeeBreakdown() {
    const { calculationResult } = this.data;

    if (!calculationResult) {
      return '';
    }

    // Only handle CalculationViewResult (single pricing)
    if (this._isCalculationViewResult(calculationResult)) {
      const { fees, options, totalAmount, subTotal } = calculationResult;
      const feeArray = Object.values(fees);
      const orderedFees = orderFeesPostCalculation(feeArray, options);
      const feeRows = orderedFees.map((fee) => this._renderFee(fee)).join('');
      const totalTypePrefix = this._getTotalTypePrefix();

      return `
        <table style="padding: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif; margin: 30px 0 30px; width: 100%; border-collapse: collapse; border-top: 2px solid rgba(0, 0, 0, 0.87); border-bottom: 2px solid rgba(0, 0, 0, 0.87);" width="100%">
          ${this._renderFeeRow(`${totalTypePrefix}Subtotal`, subTotal, false)}
          ${feeRows}
          ${this._renderFeeRow(`${totalTypePrefix}Total`, totalAmount, true)}
        </table>
      `;
    }

    return '';
  }

  /**
   * @private
   * @description Render a single fee if display is true
   * @param {Fee} fee - Fee object
   * @returns {string} Fee row HTML or empty string
   */
  _renderFee(fee) {
    if (!fee.display) return '';

    // @ts-ignore - feeSettings can be partial, getCustomLabel handles undefined gracefully
    const feeLabel = getCustomLabel(fee.name, this.data.feeSettings) || fee.name || 'Fee';

    const isTotal = false;
    return this._renderFeeRow(feeLabel, fee.amount || '0.00', isTotal);
  }

  /**
   * @private
   * @description Render fee row HTML
   * @param {string} label - Fee label
   * @param {string} amount - Fee amount
   * @param {boolean} isTotal - Whether this is the total row
   * @returns {string} Fee row HTML
   */
  _renderFeeRow(label, amount, isTotal = false) {
    const fontWeight = isTotal ? 'bold' : 'normal';
    const fontSize = isTotal ? '16px' : '14px';
    const padding = isTotal ? '10px 0' : '8px 0';

    return `
      <tr style="padding: 0; margin: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif;">
        <td style="margin: 0; box-sizing: border-box; font-family: sans-serif; text-align: left; padding: ${padding}; font-size: ${fontSize}; font-weight: ${fontWeight};">
          ${escapeHtml(label)}
        </td>
        <td style="margin: 0; box-sizing: border-box; font-family: sans-serif; text-align: right; padding: ${padding}; font-size: ${fontSize}; font-weight: ${fontWeight};">
          ${formatCurrency(amount)}
        </td>
      </tr>
    `;
  }

  /**
   * @private
   * @description Get payment type display text
   * @returns {string} Payment type display text
   */
  _getPaymentTypeDisplay() {
    const { paymentData } = this.data;
    const paymentType = paymentData?.paymentMethod?.paymentType;

    if (!paymentType) return 'N/A';

    /** @type {Record<string, string>} */
    const PAYMENT_TYPE_DISPLAY = {
      credit: 'Credit Card',
      debit: 'Debit Card',
      ach: 'Bank Transfer',
      cash: 'Cash',
      '': '',
    };

    // Check for card carrier (e.g., VISA, MASTERCARD)
    const cardCarrier = paymentData?.paymentMethod?.binCodesResponse?.card;
    if ((paymentType === 'credit' || paymentType === 'debit') && cardCarrier) {
      return cardCarrier.toUpperCase();
    }

    return PAYMENT_TYPE_DISPLAY[paymentType] || 'N/A';
  }

  /**
   * @private
   * @description Get formatted date string
   * @returns {string} Formatted date (MM/DD/YYYY)
   */
  _getFormattedDate() {
    // Prefer explicit transaction date supplied in ReceiptData
    const txDate = this.data.transactionDate;
    const d = txDate ? new Date(txDate) : new Date();

    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  /**
   * @private
   * @description Get total type prefix for dual pricing
   * @returns {string} Total type prefix ('Card ' | 'ACH ' | '')
   */
  _getTotalTypePrefix() {
    const { calculationResult, paymentData } = this.data;

    if (!calculationResult?.options?.dualPricing) {
      return '';
    }

    const paymentTypeMapper = {
      credit: 'Card ',
      debit: 'Card ',
      ach: 'ACH ',
      cash: 'Cash ',
      '': '',
    };

    // Prefer calculationResult.paymentType, then paymentData; if missing, return no prefix
    /** @type {keyof typeof paymentTypeMapper} */
    const paymentTypeRaw = calculationResult?.paymentType || paymentData?.paymentMethod?.paymentType || '';

    const displayPaymentType = paymentTypeMapper[paymentTypeRaw] || '';

    return displayPaymentType;
  }

  /**
   * @private
   * @description Render footer with thank you message and location name
   * @returns {string} Footer HTML
   */
  _renderFooter() {
    return `
      <p style="padding: 0; margin: 0; box-sizing: border-box; font-family: sans-serif; font-size: 16px; font-weight: 600; margin-bottom: 10px; text-align: center;">
        Thank you for your business.
      </p>
      ${
        this.data.locationName
          ? `
      <p style="padding: 0; margin: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif; font-weight: 600; margin-bottom: 5px; margin-top: 30px;">
        Regards,
      </p>
      <p style="padding: 0; margin: 0; box-sizing: border-box; font-size: 14px; font-family: sans-serif; font-weight: 600; margin-bottom: 5px; margin-top:5px;">
        ${escapeHtml(this.data.locationName)}
      </p>
      `
          : ''
      }
    `;
  }
}

/**
 * @class ReceiptService
 * @description Namespace for receipt-related services
 */
class ReceiptService {
  static Receipt = Receipt;
}

module.exports = ReceiptService;
